'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';
import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { FavoritesSignInDialog } from '@eliadmin/core/src/components/dlz/dialogs/favorites-sign-in-dialog';
import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { checkIfInLocalStorage } from '@eliadmin/core/src/utils/favorite-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { FiHeart } from 'react-icons/fi';

/*
 * Core override – identical to the core button except for a guest when the store keeps favourites
 * for signed-in customers only (`enable_add_to_favorite_when_not_logged_in` off): instead of a
 * redirect to the sign-in page the guest gets the „Prihláste sa“ dialog (Figma 60152:16868).
 */

type AddToFavoritesButtonProps = {
  slug: string;
  className?: string;
  isFavorite?: boolean;
  productId?: number;
  showText?: boolean;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  buttonClassName?: string;
};

function useSignInDialog() {
  const open = useDialogStore(state => state.open);
  return () => open({ id: FavoritesSignInDialog.id, content: <FavoritesSignInDialog /> });
}

export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const hasCustomer = useCustomerStore(state => !!state.me?.authToken);
  const startedAsGuest = useRef(!hasCustomer);
  const [activated, setActivated] = useState(hasCustomer);
  useEffect(() => {
    if (hasCustomer) setActivated(true);
  }, [hasCustomer]);
  // Do not unmount an authenticated mutation owner during logout. A guest
  // browsing a listing never needs a disabled query/mutation on every card.
  return hasCustomer || activated ? (
    <CustomerFavoritesButton {...props} refetchOnMount={startedAsGuest.current} />
  ) : (
    <GuestFavoritesButton {...props} />
  );
}

function GuestFavoritesButton(props: AddToFavoritesButtonProps) {
  const enabled = useGlobalStore(
    state =>
      state.templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled,
  );
  const openSignIn = useSignInDialog();
  const [isFavorite, setFavorite] = useState(!!props.isFavorite);
  useEffect(() => {
    const sync = () =>
      setFavorite(
        enabled && props.productId != null
          ? !!checkIfInLocalStorage(props.productId)
          : !!props.isFavorite,
      );
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [enabled, props.productId, props.isFavorite]);
  return (
    <FavoritesButtonView
      {...props}
      isFavorite={isFavorite}
      onClick={event => {
        event.stopPropagation();
        if (!enabled) {
          openSignIn();
          return;
        }
        if (props.productId == null) return;
        const stored = JSON.parse(localStorage.getItem('favoriteProducts') || '[]') as number[];
        const favorites = stored.includes(props.productId)
          ? stored.filter(id => id !== props.productId)
          : [...stored, props.productId];
        localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
        setFavorite(favorites.includes(props.productId));
        window.dispatchEvent(new Event('storage'));
      }}
    />
  );
}

function CustomerFavoritesButton(props: AddToFavoritesButtonProps & { refetchOnMount: boolean }) {
  const authToken = useCustomerStore(state => state.me?.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const openSignIn = useSignInDialog();
  const queryClient = useQueryClient();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const me = useCustomerStore(s => s.me);

  const { data: product } = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, props.slug],
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken,
      }),
    enabled: !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    refetchOnMount: props.refetchOnMount,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: addProductToFavorites,
    onSettled: (data, error) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCT_DETAIL, props.slug] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.FAVORITE_PRODUCTS] });
      if (!error) {
        queryClient.setQueryData([CacheKey.PRODUCT_DETAIL, props.slug], {
          ...product,
          data: {
            ...product?.data,
            is_favorite: !isFavorite,
          },
        });
      }
    },
  });

  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(
    (props.productId &&
      ((!me &&
        templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled &&
        checkIfInLocalStorage(props.productId)) ||
        product?.data.is_favorite ||
        props.isFavorite)) ??
      false,
  );
  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!authToken) {
      if (templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled) {
        const existingFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');

        if (existingFavorites.includes(props.productId)) {
          const updatedFavorites = existingFavorites.filter((id: number) => id !== props.productId);

          setIsFavorite(false);

          localStorage.setItem('favoriteProducts', JSON.stringify(updatedFavorites));
          window.dispatchEvent(new Event('storage'));
        } else {
          existingFavorites.push(props.productId);

          setIsFavorite(true);

          localStorage.setItem('favoriteProducts', JSON.stringify(existingFavorites));
          window.dispatchEvent(new Event('storage'));
        }
      } else {
        openSignIn();
      }
    } else {
      mutate({ productSlug: props.slug, authToken, locale });
      setIsFavorite(!isFavorite);
    }
  }

  return (
    <FavoritesButtonView
      {...props}
      isFavorite={isFavorite}
      isMutating={isMutating}
      productName={product?.data?.name}
      onClick={handleAddToFavorites}
    />
  );
}

function FavoritesButtonView(
  props: AddToFavoritesButtonProps & {
    isMutating?: boolean;
    productName?: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
  },
) {
  const { t } = useTranslations();
  const { isFavorite, isMutating } = props;
  const action = isFavorite ? t('text532') : t('text531');
  const buttonLabel = props.showText ? action : `${action} ${props.productName ?? props.slug}`;
  return (
    <Button
      variant={props.buttonVariant ?? 'outline'}
      size={props.buttonSize ?? 'icon-sm'}
      onClick={props.onClick}
      className={cn('px-3', props.buttonClassName)}
      aria-label={buttonLabel}
      aria-pressed={!!isFavorite}
    >
      {isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="flex flex-row">
          <FiHeart
            className={cn(
              'h-5 w-5 cursor-pointer transition-colors duration-200 ease-in-out  hover:text-muted-foreground',
              'text-foreground',
              isFavorite ? 'fill-[#0F1012] stroke-[#0F1012]' : 'fill-none',
              props.className,
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {props.showText && (
            <span className="link ml-2 underline">{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
